import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  Ausschreibungstextes,
  Broschures, CadDatens,
  ConstructionTypes, ExtendedProperties,
  IContactDescription, Keyword,
  IAddressData, IContact,
  LeadData, Musters,
} from '../../../../models/leads/leads.model';
import { LEADS_DATA_SERVICE, LeadsDataService } from '../../../../services/leads/leads-data.service';
import { CONTACTS_DATA_SERVICE, ContactsDataService } from '../../../../services/contacts/contacts-data.service';
import { MatDialog } from '@angular/material/dialog';
import { ActionHelper } from '../../../../helper/action.helper';
import { LeadHelper } from '../../../../helper/lead.helper';
import { TitleHelper } from '../../../../helper/title.helper';
import { ContactHelper } from '../../../../helper/contact.helper';
import { AddressHelper } from '../../../../helper/address.helper';

@Component({
  selector: 'fi-contact-type-card',
  templateUrl: './contact-type-card.component.html',
  styleUrls: ['./contact-type-card.component.scss'],
  standalone: false,
})
export class ContactTypeCardComponent implements OnChanges {

  @Input() selectedLeadDetails: LeadData | undefined;
  @Input() contactToDisplay: IContact;
  @Input() extendedProperties: ExtendedProperties;
  @Input() constructionTypes: ConstructionTypes[] = [];
  @Input() selectedLeadId: number;
  @Input() isDealerPartner: boolean;
  @Input() isPremiumProjectPartner: boolean;
  @Input() canEdit: boolean;
  @Input() userId: number;
  @Input() addressToDisplay: string;
  @Input() consultationTypeName: string;
  @Input() selectedLinkedContactId: number;
  @Input() broschures: Broschures[];
  @Input() ausschreibungstextes: Ausschreibungstextes[];
  @Input() cadDatens: CadDatens[];
  @Input() musters: Musters[];


  @Output() connectedContactClicked = new EventEmitter<number>();
  @Output() leadUpdated = new EventEmitter<number>();

  leadOfferType = LeadHelper.LEAD_TYPE_OFFER;
  leadTypeTranslation: string;
  leadTypeIconUrl: string;
  constructionIconUrl: string;
  tags: Keyword[] = [];
  showSharedLeadBox: boolean;
  readonly leadTypeRelationshipManagement = LeadHelper.LEAD_TYPE_PLANER_RELATIONSHIP;

  private loadedTags = new Map<number, Keyword[]>();

  constructor(
    @Inject(LEADS_DATA_SERVICE) private leadsDataService: LeadsDataService,
    @Inject(CONTACTS_DATA_SERVICE) private contactsDataService: ContactsDataService,
    private dialog: MatDialog,
  ) {

  }

  /**
   * The full header data may only be elaborated by the editor of an enquiry that is not done yet.
   */
  get canEditHeaderData(): boolean {
    return this.canEdit && this.userId === this.selectedLeadDetails?.Editor?.Id;
  }

  /**
   * Premium partners may open the header overlay of any enquiry - including completed ones - to
   * share it with Finstral, as long as the enquiry can be shared at all (designer or trade contact).
   */
  get canShareWithFinstral(): boolean {
    return this.isPremiumProjectPartner && (
      this.contactToDisplay?.ContactTypeId === ContactHelper.CONTACT_TYPE_PLANER ||
      this.contactToDisplay?.ContactTypeId === ContactHelper.CONTACT_TYPE_TRADE
    );
  }

  ngOnChanges( changes: SimpleChanges ): void {
    if ( changes.selectedLeadDetails && this.selectedLeadDetails ) {
      this.getTypeTranslationAndIcon();
      this.getConsultationTypeIcon(this.selectedLeadDetails.Lead?.ConstructionTypeId);
      this.checkSharedLead();
    }
    if ( changes.selectedLeadId && this.selectedLeadId ) {
      this.getTags(this.selectedLeadId);
    }

  }

  openContactDetails( id?: number ): void {
    this.selectedLinkedContactId = id;
    this.connectedContactClicked.emit(id)
  }

  disconnectContact( contact: IContactDescription ): void {
    const leadId = this.selectedLeadDetails?.Lead?.Id;

    if ( !contact?.Id || !leadId ) {
      return;
    }

    ActionHelper.showAction('DELETE-CONFIRM', this.dialog, {
      customMessageSnippet: 'REQUEST_DETAIL_CONTACT_LINK_DELETE_TITLE',
      customDescriptionSnippet: 'REQUEST_DETAIL_CONTACT_LINK_DELETE_TEXT',
      customDescriptionParams: { ConnectedContactName: `${contact.FirstName ?? ''} ${contact.LastName ?? ''}`.trim() },
      customConfirmSnippet: 'REQUEST_DETAIL_LEAD_HISTORY_NOTE_DELETE_CONFIRM',
      customCancelSnippet: 'REQUEST_DETAIL_LEAD_HISTORY_NOTE_DELETE_CANCEL',
    })?.afterClosed().subscribe(confirmed => {
      if ( confirmed ) {
        this.contactsDataService.disconnectContact(contact.Id, leadId).subscribe(( { success } ) => {
          if ( success ) {
            this.leadUpdated.emit(leadId);
          }
        });
      }
    });
  }

  deleteTag( tag: Keyword ): void {
    this.leadsDataService.deleteKeywords([tag.Text], this.selectedLeadId).subscribe(() => {
      this.getTags(this.selectedLeadId);
    });
  }

  openTagsDialog(): void {
    const actionData = {
      leadDetails: this.selectedLeadDetails?.Lead,
      tags: this.tags,
    };
    ActionHelper.showAction('FI-TAGS', this.dialog, actionData).afterClosed().subscribe(returnVal => {
      if ( returnVal ) {
        this.getTags(this.selectedLeadId);
      }
    });
  }

  onEditLead(): void {
    const id = this.selectedLeadDetails?.Lead?.Id;

    if ( id ) {
      ActionHelper.openEditLeadDialog(
        this.dialog,
        {
          leadId: id,
          tags: this.tags,
          broschures: this.broschures,
          musters: this.musters,
          cadDatens: this.cadDatens,
          ausschreibungstextes: this.ausschreibungstextes,
          canEditFields: this.canEditHeaderData
        },
        ( message ) => {
          if ( message === 'updateWithTags' ) {
            this.getTags(this.selectedLeadId);
          }
          this.leadUpdated.emit(id);
        });
    }
  }

  openMapNewTab(): void {
    const mapAddress = this.getMapAddress();

    if ( !mapAddress ) {
      return;
    }

    const addressUrl = AddressHelper.getMapUrl(mapAddress);
    window.open(addressUrl);
  }

  onSharedBoxClick( event: MouseEvent ): void {
    const target = event.target as HTMLElement;
    if ( target.closest('u') !== null ) {
      this.onEditLead()
    }
  }

  private checkSharedLead(): void {
    // TODO verify with product: this info banner still uses `isDealerPartner` while the share
    //  eligibility (canShareWithFinstral / edit-lead checkbox) uses `isPremiumProjectPartner`.
    //  Align it here as well if the banner should only be shown to premium partners.
    this.showSharedLeadBox = this.contactToDisplay?.ContactTypeId !== ContactHelper.CONTACT_TYPE_PRIVATE
      && this.isDealerPartner
      && this.selectedLeadDetails.Lead?.IsShared;
  }

  private getMapAddress(): IAddressData | undefined {
    const address = this.selectedLeadDetails?.Lead?.Address;

    if ( address && Object.keys(address).length > 0 ) {
      return address;
    }

    return (this.selectedLeadDetails?.Contact ?? this.contactToDisplay) as IAddressData | undefined;
  }

  private getConsultationTypeIcon( typeId: number ): void {
    this.constructionIconUrl = LeadHelper.getConstructionIconUrl(typeId, true, false);
  }

  private getTags( leadId: number ): void {
    this.leadsDataService.getKeywords(leadId).subscribe(( { success, data } ) => {
      if ( success ) {
        this.loadedTags.set(leadId, data);
        this.tags = data;
      }
    });
  }

  private getTypeTranslationAndIcon(): void {
    this.leadTypeTranslation = TitleHelper.getLeadTypeTranslation(this.selectedLeadDetails.Lead?.LeadTypeId)
    this.leadTypeIconUrl = this.selectedLeadDetails.Lead?.Done ?
      LeadHelper.getClosedAssetImage(this.selectedLeadDetails.Lead?.LeadTypeId) :
      LeadHelper.getAssetImage(this.selectedLeadDetails.Lead?.LeadTypeId);
  }

}
